/**
 * Canonical primary-attribute heuristic — the single source of truth used by
 * CollectionTable, RecordDetailPanel, and the reference-editor's search filter.
 *
 * Resolution order (same as Attio):
 *   1. The attribute named "name" (case-insensitive), else the one whose slug is "name"
 *   2. The first text-typed attribute
 *   3. The first attribute
 *
 * This helper replaces three independent copies of the same heuristic that
 * previously lived in CollectionTable (column-based), RecordDetailPanel
 * (attribute-based), and reference-editor (attribute-based). Clean-as-you-go:
 * delete each copy when you migrate it to this helper.
 */

/** Returns the primary attribute object from a list. Works with any object
 *  that has `name` and `type` string fields — covers both the snake_case
 *  Attribute schema (attribute_id) and the camelCase Column shape (attributeId). */
export function resolvePrimaryAttribute<T extends { name: string; type: string }>(
  attrs: ReadonlyArray<T>,
): T | undefined {
  return (
    attrs.find((a) => a.name.toLowerCase() === 'name') ??
    // the same convention by its stable key: an attribute RENAMED for people
    // ("Title") keeps its `name` slug (Work screen, 2026-10-21)
    attrs.find((a) => (a as { slug?: unknown }).slug === 'name') ??
    attrs.find((a) => a.type === 'text') ??
    attrs[0]
  )
}

/** Convenience wrapper: returns `attribute_id` from an Attribute-shaped array. */
export function resolvePrimaryAttributeId(
  attrs: ReadonlyArray<{ attribute_id: string; name: string; type: string }>,
): string | undefined {
  return resolvePrimaryAttribute(attrs)?.attribute_id
}
